import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setErrors({});
    setSuccessMessage('');
    setIsLoading(true);

    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });

      if (error) {
        setErrors({ submit: error?.message });
        setIsLoading(false);
        return;
      }

      setSuccessMessage('Password reset link has been sent to your email. Please check your inbox and spam folder.');
      setIsLoading(false);
    } catch (err) {
      setErrors({ submit: err?.message || 'An error occurred. Please try again.' });
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setEmail(e?.target?.value);
    
    if (errors?.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
    if (errors?.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl shadow-elevated border border-border p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
                aria-label="Go back to login"
              >
                <Icon name="ArrowLeft" size={20} strokeWidth={2} />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  Forgot Password
                </h2>
                <p className="text-sm text-muted-foreground">
                  Reset your account password
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} className="text-primary mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <p className="text-sm text-foreground">
                    Enter your registered email address and we'll send you a secure link to reset your password.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                name="email"
                label="Email Address"
                placeholder="Enter your registered email"
                value={email}
                onChange={handleChange}
                error={errors?.email}
                required
                disabled={isLoading}
              />

              {successMessage && (
                <div className="flex items-start gap-2 p-4 bg-success/10 border border-success/20 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-sm text-success">{successMessage}</p>
                </div>
              )}

              {errors?.submit && (
                <div className="flex items-start gap-2 p-4 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-sm text-error">{errors?.submit}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth"
                disabled={isLoading}
              >
                Back to Login
              </button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={18} className="text-muted-foreground mt-0.5" strokeWidth={2} />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Security Notice:</strong> The password reset link will expire in 1 hour. If you don't receive the email within a few minutes, please check your spam folder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;